import { dehydrate, QueryClient } from '@tanstack/react-query'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import slugToTitle from '@vangst/lib/slugToTitle'
import { fetchPage } from '@vangst/services/oogst/page'
import { fetchPages } from '@vangst/services/oogst/pages'
import {
  ElasticSearchEndpoints,
  ElasticSearchVars,
  prefetchElasticSearch,
} from '@vangst/services/oogst/search/useElasticSearch'
import {
  CountriesEnum,
  RobotsEnum,
  StatesEnum,
} from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { slugify, STATES_WITH_CITIES } from '@vangst/services/statesAndCities'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import Busy from '../../../../../components/feedback/Busy'
import Breadcrumbs from '../../../../../components/navigation/Breadcrumbs'
import ClickyExplore from '../../../../../components/navigation/ClickyExplore'
import ClickyLink from '../../../../../components/navigation/ClickyLink'
import ListPseudos from '../../../../../components/views/ListPseudos'
import Markdown from '../../../../../components/views/Markdown'
import PageHeader from '../../../../../components/views/PageHeader'
import Meta from '../../../../../domains/application/Meta'
import SearchRenderer from '../../../../../domains/search/SearchRenderer'

type StaticProps = GetStaticPropsContext<{ country: string; state: string }>

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

type SearchType = {
  title: string
  endpoint: ElasticSearchEndpoints
  variables: ElasticSearchVars
}

export async function getStaticProps({ params }: StaticProps) {
  const queryClient = new QueryClient()
  const country = params?.country
  const state = params?.state
  const rawUrl = __filename
    .split('pages')[1]
    ?.replace('.js', '')
    .replace('[country]', country || '')
    .replace('[state]', state || '')

  const stateWithCities = STATES_WITH_CITIES.states.find(
    (s) =>
      slugify(s.name) === state?.toLowerCase() ||
      s.abbreviation === state?.toUpperCase(),
  )
  const stateAbbr = stateWithCities?.abbreviation as StatesEnum
  const stateName = stateWithCities?.name
  const stateSlug = stateWithCities
    ? slugify(stateWithCities.abbreviation)
    : undefined

  const pages = await fetchPages(queryClient, {
    rawUrlEndsWith: `-${stateAbbr.toLowerCase()}`,
  })

  const { getPage } = await fetchPage(queryClient, { rawUrl })
  const page = {
    ...getPage,
    metaInfo: {
      ...getPage?.metaInfo,
      robots: getPage?.metaInfo.robots ?? RobotsEnum.IndexFollow,
      canonicalUrl:
        getPage?.metaInfo.canonicalUrl ??
        `https://app.vangst.com/jobs/browse/${country ?? 'us'}/${stateSlug}`,
    },
    title: getPage?.title ?? `Employment, Cannabis Jobs in ${stateName}`,
    subtitle:
      getPage?.subtitle ??
      `Looking for cannabis jobs in ${stateName}? Vangst offers jobs in the cannabis industry across ${stateName}. Explore diverse job openings including budtenders, growers, dispensary managers, and more.`,
  }

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.JOBS, text: 'Jobs' },
    { href: Routes.JOBS_BROWSE, text: 'Browse Cannabis Jobs' },
    {
      href: `/jobs/browse/${country ?? 'us'}` as Routes,
      text: country?.toUpperCase() ?? 'US',
    },
    { href: null, text: stateAbbr ?? 'State' },
  ]

  const search: SearchType = {
    title: `Cannabis Jobs in ${stateName}`,
    endpoint: 'searchJobPostings',
    variables: {
      first: 12,
      matchLocation: {
        state: stateAbbr ?? undefined,
        country: CountriesEnum.UnitedStates,
      },
    },
  }
  const es = await prefetchElasticSearch(
    queryClient,
    search.endpoint,
    search.variables,
  )
  const links = {
    us: !es?.nodes?.length
      ? {
          label: 'Browse Cannabis Jobs in the US',
          href: '/jobs/browse/us',
        }
      : null,
  }

  return {
    props: {
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient), true),
      crumbs,
      links,
      page,
      pages,
      search,
      stateName,
    },
    notFound: country !== 'us' || stateWithCities == null,
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function Page(props: PropsType) {
  const { crumbs, links, page, pages, search, stateName } = props

  if (!page || !search)
    return (
      <article className="contain-xyc mood-white">
        <Busy />
      </article>
    )

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo.description}
        image={page.pagePhotos?.[0]?.url}
        publishDate={page.postedAt}
        robots={page.metaInfo.robots}
        title={page.metaInfo.title ?? page.title}
      />
      <Breadcrumbs crumbs={crumbs} />
      <article className="mood-white">
        <PageHeader
          icon="pin drop"
          title={page.title}
          subtitle={page.subtitle}
          description={page.description}
          imgAlt={page.pagePhotos?.[0]?.alt}
          imgSrc={page.pagePhotos?.[0]?.url}
        />

        <div className="contain-xyc flow-y-sm">
          <h2 className="heading-md">{search.title}</h2>
          {links.us ? (
            <ClickyExplore href={links.us.href}>{links.us.label}</ClickyExplore>
          ) : (
            <SearchRenderer
              as="card"
              at="infinite-preview"
              classNameList="grid-three-up min-h-64"
              endpoint={search.endpoint}
              variables={search.variables}
            />
          )}
        </div>

        <section className="contain-xyc flow-y-sm mood-white">
          <h2 className="heading-md">
            Local {stateName} Cannabis Jobs Reports
          </h2>
          <ListPseudos as="•" at="after" className="flex flex-wrap gap-2">
            {pages.records.map((record, i) => {
              return (
                <li key={record?.id ?? record?.rawUrl ?? `rec-${i}`}>
                  <ClickyLink
                    className="link-blue-dark underline"
                    href={record?.rawUrl ?? '/jobs'}
                  >
                    {slugToTitle(record?.rawUrl ?? 'Search Jobs')}
                  </ClickyLink>
                </li>
              )
            })}
          </ListPseudos>
        </section>

        {page.markdownContent && (
          <section className="contain-xyc mood-white">
            <div className="text-sm">
              <Markdown renderType="basic">{page.markdownContent}</Markdown>
            </div>
          </section>
        )}
      </article>
    </>
  )
}
